/**
 * Module Context
 * ==============
 * Provides module information throughout the application.
 * Used to conditionally show/hide menu items and protect routes
 * based on enabled backend modules.
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import config from '../config';
import { getAuthToken } from '../utils/authStorage';

const ModuleContext = createContext();

export const ModuleProvider = ({ children }) => {
  const [enabledModules, setEnabledModules] = useState([]);
  const [allowedScreens, setAllowedScreens] = useState([]);
  const [moduleInfo, setModuleInfo] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchModules = useCallback(async () => {
    try {
      const token = getAuthToken() || localStorage.getItem('token');
      if (!token) {
        // No token - user not logged in, set defaults (all modules for now)
        setEnabledModules(['core', 'basic', 'attendance', 'reports', 'dashboard', 'payments']);
        setAllowedScreens([
          'login', 'members', 'manage_member', 'member_list', 'view_member',
          'subscriptions', 'manage_subscription', 'subscription_list',
          'disciplines', 'manage_discipline', 'discipline_list',
          'branches', 'manage_branch', 'branch_list',
          'families', 'family_list',
          'attendance', 'attendance_list', 'attendance_kiosk', 'mark_attendance',
          'reports', 'generate_reports', 'discipline_report', 'attendance_report',
          'dashboard', 'home',
          'payments', 'member_payment', 'invoices', 'payment_history'
        ]);
        setLoading(false);
        return;
      }

      const response = await axios.get(`${config.apiBaseUrl}/api/enabled-modules`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setEnabledModules(response.data.enabled_modules || []);
      setAllowedScreens(response.data.allowed_screens || []);
      setModuleInfo(response.data.module_info || {});
      setError(null);
    } catch (err) {
      console.error('Failed to fetch modules:', err);
      // On error, default to all modules enabled (backward compatible)
      setEnabledModules(['core', 'basic', 'attendance', 'reports', 'dashboard', 'payments']);
      setAllowedScreens([
        'login', 'members', 'manage_member', 'member_list', 'view_member',
        'subscriptions', 'manage_subscription', 'subscription_list',
        'disciplines', 'manage_discipline', 'discipline_list',
        'branches', 'manage_branch', 'branch_list',
        'families', 'family_list',
        'attendance', 'attendance_list', 'attendance_kiosk', 'mark_attendance',
        'reports', 'generate_reports', 'discipline_report', 'attendance_report',
        'dashboard', 'home',
        'payments', 'member_payment', 'invoices', 'payment_history'
      ]);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchModules();
  }, [fetchModules]);

  /**
   * Check if a specific module is enabled
   * @param {string} moduleName - The module name to check
   * @returns {boolean} - True if module is enabled
   */
  const isModuleEnabled = useCallback((moduleName) => {
    return enabledModules.includes(moduleName);
  }, [enabledModules]);

  /**
   * Check if a specific screen is allowed
   * @param {string} screenName - The screen name to check
   * @returns {boolean} - True if screen is allowed
   */
  const isScreenAllowed = useCallback((screenName) => {
    return allowedScreens.includes(screenName);
  }, [allowedScreens]);

  /**
   * Refresh module information (call after login)
   */
  const refreshModules = useCallback(() => {
    setLoading(true);
    fetchModules();
  }, [fetchModules]);

  const value = {
    enabledModules,
    allowedScreens,
    moduleInfo,
    loading,
    error,
    isModuleEnabled,
    isScreenAllowed,
    refreshModules
  };

  return (
    <ModuleContext.Provider value={value}>
      {children}
    </ModuleContext.Provider>
  );
};

/**
 * Hook to access module context
 * @returns {Object} Module context value
 */
export const useModules = () => {
  const context = useContext(ModuleContext);
  if (!context) {
    throw new Error('useModules must be used within a ModuleProvider');
  }
  return context;
};

export default ModuleContext;
